import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const KEY = "salihi_cookie_prefs_v1";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function savePrefs(prefs) {
  localStorage.setItem(KEY, JSON.stringify({ ...prefs, ts: Date.now() }));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const p = loadPrefs();
    if (!p) setVisible(true);
    else { setAnalytics(!!p.analytics); setMarketing(!!p.marketing); }
  }, []);

  const acceptAll = () => { savePrefs({ essential: true, analytics: true, marketing: true, decision: "all" }); setVisible(false); setShowPrefs(false); };
  const rejectNonEssential = () => { savePrefs({ essential: true, analytics: false, marketing: false, decision: "essential" }); setVisible(false); setShowPrefs(false); };
  const savePreferences = () => { savePrefs({ essential: true, analytics, marketing, decision: "custom" }); setVisible(false); setShowPrefs(false); };

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      {!showPrefs && (
        <div data-testid="cookie-banner"
             className="fixed left-4 right-4 md:left-6 md:right-auto md:max-w-xl bottom-4 md:bottom-6 z-[60]
                        bg-navy-dark text-white shadow-[0_25px_60px_-20px_rgba(0,0,0,0.55)] border border-gold/30">
          <div className="p-6 md:p-7">
            <div className="flex items-start gap-3">
              <Cookie size={22} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-serif text-xl leading-tight mb-2">We value your privacy</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Salihi Group uses essential cookies for site functionality, and optional cookies to help us understand
                  how visitors use our site. You can adjust your preferences below or accept all.
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button className="btn-gold flex-1 justify-center text-xs !py-3" onClick={acceptAll} data-testid="cookie-accept-all">Accept All</button>
              <button className="btn-outline-white flex-1 justify-center text-xs !py-3" onClick={rejectNonEssential} data-testid="cookie-reject">Reject Non-Essential</button>
            </div>
            <button className="mt-4 text-gold text-xs uppercase tracking-[0.18em] font-medium editorial-link"
                    onClick={() => setShowPrefs(true)} data-testid="cookie-manage">
              Manage Preferences
            </button>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showPrefs && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy-dark/70 backdrop-blur-sm"
             data-testid="cookie-preferences-modal"
             onClick={() => setShowPrefs(false)}>
          <div className="bg-white max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-7 py-5 border-b border-softgray">
              <h2 className="font-serif text-2xl text-navy">Cookie Preferences</h2>
              <button onClick={() => setShowPrefs(false)} aria-label="Close" className="text-navy hover:text-gold">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <div className="px-7 py-6 space-y-5">
              <PrefRow title="Essential" desc="Required for the site to function. Cannot be disabled." locked checked />
              <PrefRow title="Analytics" desc="Help us understand how visitors interact with the site."
                       checked={analytics} onChange={() => setAnalytics((v) => !v)} testId="pref-analytics" />
              <PrefRow title="Marketing" desc="Used to deliver relevant communications and measure campaigns."
                       checked={marketing} onChange={() => setMarketing((v) => !v)} testId="pref-marketing" />
            </div>
            <div className="px-7 py-5 border-t border-softgray flex gap-3 justify-end">
              <button className="btn-outline-gold text-xs" onClick={rejectNonEssential} data-testid="pref-reject">Reject All</button>
              <button className="btn-gold text-xs" onClick={savePreferences} data-testid="pref-save">Save Preferences</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PrefRow({ title, desc, checked, onChange, locked = false, testId }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="font-serif text-lg text-navy">{title}</div>
        <p className="text-sm text-mutedink mt-0.5 leading-relaxed">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={locked}
        disabled={locked}
        onClick={onChange}
        data-testid={testId}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-300 ${checked ? "bg-navy" : "bg-softgray"} ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all duration-300 ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
